import { useMutation } from "@apollo/client"
import produce from "immer"
import pluralize from "pluralize"
import {
  PathDocument,
  PathItemFragmentDoc,
  useScheduleItemMutation
} from "../api/generated"
import CREATE_MESSAGE_MUTATION from "./graphql/create-message-mutation"
import DELETE_MESSAGE_MUTATION from "./graphql/delete-message-mutation"
import MESSAGES_QUERY from "./graphql/messages-query"
import UPDATE_MESSAGE_MUTATION from "./graphql/update-message-mutation"

// attempting to translate based on the cases as both compact and explicit as possible
export function displayMessageStr(message, t) {
  const unit = getMessageThresholdUnit(message)
  const num = Math.abs(getMessageThresholdNum(message))
  let unitTranslation = null

  switch (unit) {
    case "day":
      // TODO: translate
      unitTranslation = pluralize("day", num)
      break
    case "hour":
      // TODO: translate
      unitTranslation = pluralize("hour", num)
      break
    case "min":
      // TODO: translate
      unitTranslation = pluralize("minute", num)
      break
  }

  switch (message.channels.join(",")) {
    case "email":
      if (message.threshold_minutes > 0) {
        return t("email notification {{ count }} {{ unit }} after", {
          count: num,
          unit: unitTranslation
        })
      } else if (message.threshold_minutes < 0) {
        return t("email notification {{ count }} {{ unit }} before", {
          count: num,
          unit: unitTranslation
        })
      } else {
        return t("email notification")
      }
    case "notification":
      if (message.threshold_minutes > 0) {
        return t("in-app notification {{ count }} {{ unit }} after", {
          count: num,
          unit: unitTranslation
        })
      } else if (message.threshold_minutes < 0) {
        return t("in-app notification {{ count }} {{ unit }} before", {
          count: num,
          unit: unitTranslation
        })
      } else {
        return t("in-app notification")
      }
    case "email,notification":
    case "notification,email":
      if (message.threshold_minutes > 0) {
        return t("email + in-app notification {{ count }} {{ unit }} after", {
          count: num,
          unit: unitTranslation
        })
      } else if (message.threshold_minutes < 0) {
        return t("email + in-app notification {{ count }} {{ unit }} before", {
          count: num,
          unit: unitTranslation
        })
      } else {
        return t("email + in-app notification")
      }
  }
}

export const getDefaultReminderSubjectBody = (
  pathItem,
  dateAttr,
  reminder,
  reminderDefaults
) => {
  // EXAMPLE reminderDefaults shape
  // [
  //   {
  //     date_attr: "due",
  //     before_or_after: "before",
  //     date_is_passive: false,
  //     subject: "{{step.name}} is due soon",
  //     body:
  //       "Hi {{user.first_name}}, here's a friendly reminder that {{step.name}} is due soon."
  //   }
  // ]

  if (!pathItem || !reminderDefaults) {
    return ["", ""]
  }

  if (!["starts", "due"].includes(dateAttr)) {
    console.error(`Invalid dateAttr. Found: "${dateAttr}"`)
  }

  // determine beforeOrAfter
  let beforeOrAfter = null
  if (reminder.threshold_minutes >= 0) {
    beforeOrAfter = "after"
  } else {
    beforeOrAfter = "before"
  }

  // determine dateIsPassive
  let dateIsPassive = null
  if (dateAttr === "starts") {
    dateIsPassive = pathItem.passive_starts || false
  } else if (dateAttr === "due") {
    dateIsPassive = pathItem.passive_due || false
  }

  const reminderDefault = reminderDefaults.find(
    (reminderDefault) =>
      reminderDefault.date_attr === dateAttr &&
      reminderDefault.before_or_after === beforeOrAfter &&
      reminderDefault.date_is_passive === dateIsPassive
  )

  if (!reminderDefault) {
    console.error(
      `Could not find a reminder default for pathItem where:\n"dateAttr" === ${dateAttr} &&\n"beforeOrAfter" === ${beforeOrAfter} &&\n"dateIsPassive" === ${dateIsPassive}`
    )
  }

  const { subject, body } = reminderDefault
  return [subject, body]
}

export function getMessageThresholdNum(message) {
  const unit = getMessageThresholdUnit(message)
  if (unit === "day") return message.threshold_minutes / 1440
  if (unit === "hour") return message.threshold_minutes / 60
  return message.threshold_minutes
}

export function getMessageThresholdUnit(message) {
  if (message.threshold_minutes % 1440 === 0) return "day"
  if (message.threshold_minutes % 60 === 0) return "hour"
  return "min"
}

export function getMessageThresholdMinutesForUnit(num = 0, unit, dir) {
  if (unit === "day") num *= 1440
  if (unit === "hour") num *= 60
  num = Number(num)

  return Math.abs(num) * (dir === "after" ? 1 : -1)
}

export function getInitialMessageValues(
  message,
  pathItem,
  stepDateField,
  reminderDefaults
) {
  if (message) {
    return {
      threshold_num: Math.abs(getMessageThresholdNum(message)),
      threshold_unit: getMessageThresholdUnit(message),
      threshold_timing: message.threshold_minutes > 0 ? "after" : "before",
      threshold_datefield: message.based_on_step_date,
      recipient_filter: message.recipient_filter,
      channels: message.channels.join(","),
      subject: message.subject,
      body: message.body
    }
  } else {
    const message = {
      threshold_num: stepDateField === "due" && !pathItem.passive_due ? 3 : 15,
      threshold_unit:
        stepDateField === "due" && !pathItem.passive_due ? "hour" : "min",
      threshold_timing: "before",
      threshold_datefield: stepDateField,
      recipient_filter:
        stepDateField === "due" && !pathItem.passive_due
          ? "step_incomplete"
          : "all",
      channels: "email"
    }

    const [subject, body] = getDefaultReminderSubjectBody(
      pathItem,
      stepDateField,
      getMessageOutput(message),
      reminderDefaults
    )

    return {
      ...message,
      subject,
      body
    }
  }
}

export function getMessageOutput(values) {
  const message = {
    subject: values.subject,
    body: values.body,
    type: "reminder",
    recipient_filter: values.recipient_filter,
    based_on_step_date: values.threshold_datefield,
    channels: values.channels.split(","), // [email,notification]
    threshold_minutes: getMessageThresholdMinutesForUnit(
      values.threshold_num,
      values.threshold_unit,
      values.threshold_timing
    )
  }

  return message
}

export function useScheduleItem(context) {
  const [scheduleItem] = useScheduleItemMutation()
  const { cohortId, pathId, sourceId } = context

  return async function (values) {
    await scheduleItem({
      variables: {
        schedule_context: {
          cohort_id: cohortId,
          path_id: pathId,
          source_id: sourceId
        },
        starts: values.starts,
        due: values.due,
        passive_starts: values.starts ? values.passive_starts : null,
        passive_due: values.due ? values.passive_due : null
      },
      update: (proxy, result) => {
        try {
          // NOTE: yes, need to update
          const query = PathDocument
          const variables = {
            id: null,
            cohort_id: cohortId
          }
          const data = proxy.readQuery({ query, variables })
          const nextData = produce(data, (draft) => {
            const scheduledItem = result.data.scheduleItem

            const edge = draft.path.items.edges.find(
              (edge) => edge.node.source_id === scheduledItem.source_id
            )

            // Unsure if updating the edge.node in the PathDocument does the same as updating the PathItemFragmentDoc, so this may be overkill
            const fragment = {
              fragment: PathItemFragmentDoc,
              fragmentName: "PathItem",
              id: "PathItem:" + edge.node.id
            }
            const pathItemData = proxy.readFragment(fragment)
            const nextPathItemData = produce(pathItemData, (draft) => {
              draft.starts = scheduledItem.starts
              draft.due = scheduledItem.due
              draft.passive_starts = scheduledItem.passive_starts
              draft.passive_due = scheduledItem.passive_due
            })
            proxy.writeFragment({
              ...fragment,
              data: nextPathItemData
            })
          })
        } catch (error) {
          console.warn(`Failed to update cache for scheduled item`, error)
        }
      }
    })
  }
}

export function useSubmitMessage(context, onComplete) {
  const [createMessage] = useMutation(CREATE_MESSAGE_MUTATION)
  const [updateMessage] = useMutation(UPDATE_MESSAGE_MUTATION)
  const { cohortId, pathId, sourceId } = context

  return async function (id, values) {
    const message = {
      subject: values.subject,
      body: values.body,
      type: values.type,
      recipient_filter: values.recipient_filter,
      based_on_step_date: values.based_on_step_date,
      channels: values.channels,
      threshold_minutes: values.threshold_minutes
    }

    if (id) {
      await updateMessage({
        variables: {
          id: id,
          message_context: {
            cohort_id: cohortId,
            path_id: pathId,
            source_id: sourceId
          },
          message: message
        }
      })
    } else {
      await createMessage({
        variables: {
          message_context: {
            cohort_id: cohortId,
            path_id: pathId,
            source_id: sourceId
          },
          message: message
        },
        update: (proxy, result) => {
          const query = MESSAGES_QUERY
          const variables = {
            message_context: {
              cohort_id: cohortId,
              path_id: pathId,
              source_id: sourceId
            },
            type: { eq: "reminder" }
          }
          const data = proxy.readQuery({ query, variables })
          const nextData = produce(data, (draft) => {
            const newEdge = {
              node: result.data.createMessage,
              __typename: "MessageEdge"
            }
            draft.messages.edges.push(newEdge)
          })
          proxy.writeQuery({ query, variables, data: nextData })
        }
      })
    }
    onComplete && onComplete()
  }
}

export function useDeleteMessage(context, onComplete) {
  const [deleteMessage] = useMutation(DELETE_MESSAGE_MUTATION)
  const { cohortId, pathId, sourceId } = context

  return async function (id) {
    try {
      await deleteMessage({
        variables: {
          id: id,
          message_context: {
            cohort_id: cohortId,
            path_id: pathId,
            source_id: sourceId
          }
        },
        update: (proxy, result) => {
          const query = MESSAGES_QUERY
          const variables = {
            message_context: {
              cohort_id: cohortId,
              path_id: pathId,
              source_id: sourceId
            },
            type: { eq: "reminder" }
          }
          const data = proxy.readQuery({ query, variables })
          const nextData = produce(data, (draft) => {
            const i = draft.messages.edges.findIndex((x) => x.node.id == id)
            draft.messages.edges.splice(i, 1)
          })
          proxy.writeQuery({ query, variables, data: nextData })
        }
      })
      onComplete && onComplete()
    } catch (error) {
      console.log(error)
    }
  }
}
